/// <reference types="vite/client" />
import {
  AiDraftType,
  KnowledgeGapSkipReason,
  LanguageId,
} from 'lib-knowledge-management-common/graphql';

import { JudgeOutcome } from './testRun.utils';

/**
 * A canned judge result for local development, so the result screens can be
 * worked on without running the AI. Dev builds only. Turn it on from the
 * console:
 *
 *   localStorage.setItem('knowledgeGapJudgeMock', JSON.stringify({
 *     outcome: 'updates', // or 'newArticle', 'noGaps', 'notEnough', 'skipped'
 *     articleIds: ['<articles in your org, for the update drafts>'],
 *     languageId: 'en_us', // the language those articles are written in
 *   }))
 */
const STORAGE_KEY = 'knowledgeGapJudgeMock';

type MockConfig = {
  outcome?: 'updates' | 'newArticle' | 'noGaps' | 'notEnough' | 'skipped';
  articleIds?: string[];
  languageId?: string;
};

function readConfig(): MockConfig | undefined {
  if (!import.meta.env.DEV) return undefined;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as MockConfig) : undefined;
  } catch {
    return undefined;
  }
}

export async function mockJudgeOutcome(): Promise<JudgeOutcome | undefined> {
  const config = readConfig();
  if (!config) return undefined;

  await new Promise((resolve) => setTimeout(resolve, 1500));
  const [firstId = 'mock-article-1', secondId = 'mock-article-2'] =
    config.articleIds ?? [];
  const languageId = (config.languageId ?? 'en') as LanguageId;

  switch (config.outcome) {
    case 'skipped':
      return {
        __typename: 'KnowledgeGapJudgeOutcome_Skipped',
        reason: KnowledgeGapSkipReason.NoProblemOrApproach,
      };
    case 'notEnough':
      return {
        __typename: 'KnowledgeGapJudgeOutcome_NotEnoughSimilarConversations',
        similarConversationIds: [101, 102],
        required: 5,
      };
    case 'noGaps':
      return {
        __typename: 'KnowledgeGapJudgeOutcome_Judged',
        similarConversationIds: [101, 102, 103, 104],
        gaps: [],
        drafts: [],
      };
    case 'newArticle':
      return {
        __typename: 'KnowledgeGapJudgeOutcome_Judged',
        similarConversationIds: [101, 102, 103, 104],
        gaps: [
          {
            __typename: 'KnowledgeGap',
            articleId: firstId,
            aiDraftId: null,
            gapType: 'notCovered',
            gapDescription: 'Nothing explains how gift cards work.',
          },
        ],
        drafts: [
          {
            __typename: 'KnowledgeGapDraft',
            internalOnly: false,
            type: AiDraftType.NewArticle,
            languageId,
            forArticleId: null,
            collection: null,
            category: null,
            title: 'Using gift cards',
            htmlContent:
              '<p>Gift cards never expire and can be used on any order, including sale items.</p><h2>Checking your balance</h2><p>Enter the card number at checkout to see what is left on it.</p>',
            summary: 'Explains how gift cards work.',
          },
        ],
      };
    default:
      return {
        __typename: 'KnowledgeGapJudgeOutcome_Judged',
        similarConversationIds: [101, 102, 103, 104],
        gaps: [
          {
            __typename: 'KnowledgeGap',
            articleId: firstId,
            aiDraftId: null,
            gapType: 'partiallyCovered',
            gapDescription:
              'The article does not say how long sale items can be returned.',
          },
          {
            __typename: 'KnowledgeGap',
            articleId: secondId,
            aiDraftId: null,
            gapType: 'partiallyCovered',
            gapDescription: 'Refund times for card payments are missing.',
          },
          {
            __typename: 'KnowledgeGap',
            articleId: 'mock-undescribed-article',
            aiDraftId: null,
            gapType: 'partiallyCovered',
            gapDescription: null,
          },
        ],
        drafts: [
          {
            __typename: 'KnowledgeGapDraft',
            internalOnly: false,
            type: AiDraftType.ArticleUpdate,
            languageId,
            forArticleId: firstId,
            collection: {
              __typename: 'AiDraftCollection',
              id: 'mock-collection',
              name: 'Customer Service',
            },
            category: {
              __typename: 'AiDraftCategory',
              id: 'mock-category',
              name: 'Returns & Refunds',
            },
            title: 'Returning sale items',
            htmlContent:
              '<h2>Return window</h2><p>Sale items can be returned within 14 days of delivery, the same as full-price items.</p>',
            summary: 'Adds the return window for sale items.',
          },
          {
            __typename: 'KnowledgeGapDraft',
            internalOnly: false,
            type: AiDraftType.ArticleUpdate,
            languageId,
            forArticleId: secondId,
            collection: {
              __typename: 'AiDraftCollection',
              id: 'mock-collection',
              name: 'Customer Service',
            },
            category: null,
            title: 'Refunds',
            htmlContent:
              '<p>Refunds go back to the original payment method. Card refunds take up to 5 working days.</p>',
            summary: 'Adds refund times for card payments.',
          },
        ],
      };
  }
}
